export class ScrapingError extends Error {
  constructor(
    message: string,
    public code: string,
    public recoverable: boolean = true,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ScrapingError";
  }

  /**
   * Creates a formatted error message with details
   */
  getFormattedMessage(): string {
    let formattedMessage = `${this.name} [${this.code}]: ${this.message}`;
    if (this.details && Object.keys(this.details).length > 0) {
      formattedMessage += `\nDetails: ${JSON.stringify(this.details, null, 2)}`;
    }
    return formattedMessage;
  }
}

export class NetworkError extends ScrapingError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, "NETWORK_ERROR", true, details);
    this.name = "NetworkError";
  }
}

export class SelectorError extends ScrapingError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, "SELECTOR_ERROR", true, details);
    this.name = "SelectorError";
  }
}

export class TimeoutError extends ScrapingError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, "TIMEOUT_ERROR", true, details);
    this.name = "TimeoutError";
  }
}

export class ValidationError extends ScrapingError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, "VALIDATION_ERROR", false, details);
    this.name = "ValidationError";
  }
}

export class AuthenticationError extends ScrapingError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, "AUTHENTICATION_ERROR", false, details);
    this.name = "AuthenticationError";
  }
}

export class RateLimitError extends ScrapingError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, "RATE_LIMIT_ERROR", true, details);
    this.name = "RateLimitError";
  }
}

export class ProxyError extends ScrapingError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, "PROXY_ERROR", true, details);
    this.name = "ProxyError";
  }
}

export class DataExtractionError extends ScrapingError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, "DATA_EXTRACTION_ERROR", true, details);
    this.name = "DataExtractionError";
  }
}

export class DatabaseError extends ScrapingError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, "DATABASE_ERROR", false, details);
    this.name = "DatabaseError";
  }
}

/**
 * Helper function to determine if an error is a specific type of ScrapingError
 */
export function isScrapingError(error: any): error is ScrapingError {
  return error instanceof ScrapingError;
}

/**
 * Helper function to determine if an error is recoverable
 */
export function isRecoverableError(error: any): boolean {
  return error instanceof ScrapingError ? error.recoverable : false;
}

/**
 * Helper function to convert unknown errors to ScrapingError
 */
export function toScrapingError(error: any): ScrapingError {
  if (error instanceof ScrapingError) {
    return error;
  }

  // Handle network-related errors
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return new NetworkError(error.message, { originalError: error.toString() });
  }

  // Handle timeout errors
  if (
    error.name === "TimeoutError" ||
    (error.message && error.message.toLowerCase().includes("timeout"))
  ) {
    return new TimeoutError(error.message, { originalError: error.toString() });
  }

  // Default to a generic scraping error
  return new ScrapingError(
    error instanceof Error ? error.message : String(error),
    "UNKNOWN_ERROR",
    true,
    { originalError: error.toString() },
  );
}
